import { groupBy } from '../object-utils';
import * as commonEnums from '../../enums/index';
import * as appServiceLines from '../../core/service-lines';

const { ChargeTypes } = commonEnums;

const serviceLineMap = Object.freeze({
  SURG: 'surgery',
  PATH: 'pathology',
  RAD: 'radiology'
});

const FOUR_LETTER_SERVICE_LINE_MAP = Object.freeze({
  SURG: 'surgery',
  PATH: 'pathology',
  RADI: 'radiology'
});

const unknownServiceLine = appServiceLines.determineServiceLineConfig({
  name: 'unknown'
});

const handleTracking = (match: RegExpExecArray): Accelerate.ProcedureInfo => {
  if (match.length !== 6) {
    throw new Error(`tracking charge format not matched`);
  }
  if (!(match[2] in serviceLineMap)) {
    throw new Error(`Unknown service line: ${match[2]}`);
  }
  const serviceLine = appServiceLines.determineServiceLineConfig({
    name: serviceLineMap[match[2]]
  });

  return {
    identifier: match[3],
    chargeType: ChargeTypes.TRACKING,
    serviceLine
  };
};

const handleStandard = (match: RegExpExecArray): Accelerate.ProcedureInfo => {
  if (!(match[5] in FOUR_LETTER_SERVICE_LINE_MAP)) {
    throw new Error(`Unknown service line: ${match[4]}`);
  }

  const serviceLine = appServiceLines.determineServiceLineConfig({
    name: serviceLineMap[match[5]]
  });

  return {
    identifier: match[4],
    chargeType: ChargeTypes.STANDARD,
    serviceLine
  };
};

const handleLegacy = (match: RegExpExecArray): Accelerate.ProcedureInfo => {
  return {
    identifier: match[3],
    chargeType: ChargeTypes.LEGACY,
    serviceLine: unknownServiceLine
  };
};

const handleUnknown = (unparsedUcl108: string): Accelerate.ProcedureInfo => {
  return {
    identifier: unparsedUcl108,
    chargeType: ChargeTypes.UNKNOWN,
    serviceLine: unknownServiceLine
  };
};

const parseUCL108 = (ucl108: string): any => {
  if (typeof ucl108 !== 'string')
    throw new Error(`requires string, receieved ${ucl108}`);
  if (ucl108.length === 0) throw new Error(`received empty string for ucl108`);

  const fragments = ucl108.split('^');

  const matches = fragments.map(fragment => {
    // See: https://codametrix.atlassian.net/wiki/spaces/DEV/pages/2629304385/Case+Launch+Sequence
    // ------------------ Tracking Charges ------------------
    // CMX-<service_line_short_name>-<process_instance_id>-<invoice_short_guid>-<counter>
    // CMX-RAD-273894-ae389fd-5
    // 0: "CMX-RAD-273894-ae389fd-5"
    // 1: "CMX" // company
    // 2: "RAD" // service line
    // 3: "273894" // process instance
    // 4: "ae389fd" // invoice short guid
    // 5: "5" // counter
    const TRACKING_CHARGE_REGEX = /([a-zA-Z0-9]+)\-([a-zA-Z0-9]+)\-([a-zA-Z0-9]+)\-([a-zA-Z0-9]+)\-([a-zA-Z0-9]+)/gm;
    // ------------------ Standard Charges ------------------
    // CMX_INT1-T-12345-RADI
    // 0: "CMX_INT1-T12345-RADI"
    // 1: "CMX" // company
    // 2: "INT1" // environment
    // 3: "T" // charge type
    // 4: "12345" // transaction id
    // 5: "RADI" // four letter service line identifier
    const STANDARD_CHARGE_REGEX = /([a-zA-Z0-9]+)\_([a-zA-Z0-9]+)\-([T|P])[\-]?([a-zA-Z0-9]+)\-([a-zA-Z0-9]+)/gm;
    // ------------------ Legacy Format  ------------------
    // Ex: CMX-P474-PATH
    // 0: "CMX-P474-PATH"
    // 1: "CMX" // company
    // 2: "T" // charge type
    // 3: "474" // transaction id
    // 4: "PATH" // four letter service line identifier
    const LEGACY_FORMAT_REGEX = /([a-zA-Z0-9]+)\-([T|P])([0-9]+)\-([a-zA-Z0-9]{4})/gm;

    const trackingMatch = TRACKING_CHARGE_REGEX.exec(fragment);
    const standardMatch = STANDARD_CHARGE_REGEX.exec(fragment);
    const legacyMatch = LEGACY_FORMAT_REGEX.exec(fragment);

    try {
      if (trackingMatch) {
        return handleTracking(trackingMatch);
      } else if (standardMatch) {
        return handleStandard(standardMatch);
      } else if (legacyMatch) {
        return handleLegacy(legacyMatch);
      } else {
        return handleUnknown(fragment);
      }
    } catch (e) {
      // in the case of a parse exception treat as unknown.
      return handleUnknown(fragment);
    }
  });
  return groupBy(matches, 'chargeType');
};

export { parseUCL108 };
